import type { ReactNode } from "react";
import { Breadcrumb } from "@/components/base/breadcrumb/breadcrumb";
import { cx } from "@/utils/cx";

/** Nível superior da hierarquia. Sempre tem destino navegável. */
export type PageBreadcrumbAncestor = {
	label: string;
	icon?: ReactNode;
} & ({ href: string; onClick?: never } | { href?: never; onClick: () => void });

export type PageBreadcrumbsProps = {
	/** Níveis acima da página atual, do mais alto ao mais próximo. */
	ancestors: PageBreadcrumbAncestor[];
	/** Rótulo da página atual. Vira o último item, sem link, com `aria-current="page"`. */
	current: string;
	/** Nome acessível da navegação. O padrão é "Trilha de navegação". */
	label?: string;
	className?: string;
};

/**
 * Breadcrumb de página com posição padronizada.
 *
 * Deve ficar antes do `PageHeader`, no início do conteúdo, alinhado à mesma
 * margem. Reserva 16 px (`mb-4`) até o cabeçalho; o consumidor não precisa de
 * margens próprias.
 *
 * A API separa ancestrais e página atual para preservar a hierarquia: todo
 * ancestral exige destino (`href` ou `onClick`) e o item atual nunca é link.
 * O componente não inventa níveis. Use apenas quando existir ao menos um nível
 * superior navegável real; a entrada de um módulo normalmente não precisa de
 * breadcrumb.
 */
export function PageBreadcrumbs({
	ancestors,
	current,
	label = "Trilha de navegação",
	className,
}: PageBreadcrumbsProps) {
	return (
		<Breadcrumb
			ariaLabel={label}
			className={cx("mb-4", className)}
			items={[...ancestors, { label: current }]}
		/>
	);
}
